/**
 * Catalogue views and shared form utilities for Cibles.
 *
 * Contains:
 *   - ChipListEditor         — shared chip input used by all add/edit dialogs
 *   - TagChips               — read-only tag chip list (used by TargetTableRow and CollectionListView)
 *   - GenericTargetDialog    — add/edit dialog for non-custom categories
 *   - productDomains         — extract unique domains from a product row
 *   - extractDomain / getUrlPath — URL helpers for contenu domain grouping
 *   - ProductListView        — product catalogue list
 *   - ProductAddForm         — add/edit dialog for a single product
 *   - CollectionListView     — compact catalogue collection list
 *   - ContenuDomainView      — domain-grouped display for the contenu tab
 *
 * This file sits at the bottom of the import graph: EstablishmentForm, PersonneForm,
 * and TargetTableRow all import from here but CatalogueViews imports from none of them.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import DomainFavicon from '../../components/competitors/DomainFavicon';
import { TARGET_LIMITS, registrableLabel } from '@traaker/shared';
import {
  type TargetRow, type TagRegistry, type CatConfig, type ColDef,
  tagNamesOf, colorHex, CAT_BY_ID,
} from './CATEGORIES';
import { normalizeProductUrl, normalizeDomainInput } from './csv';

// ---------------------------------------------------------------------------
// ChipListEditor — alias and tag chip input used across all form dialogs
// ---------------------------------------------------------------------------

/**
 * A list of short strings, added one at a time. Two uses, which is why it takes `suggestions`:
 * aliases (cap 3, no vocabulary) and tags (cap 20, offered from the brand's registry).
 *
 * Dedupe is case-insensitive because that is the writer's OWN identity rule for tags
 * (`_resolve_tag_ids` casefolds and trims), so accepting "Franchise" next to "franchise" here
 * would show two chips that the save then collapses into one — the form would appear to lose
 * an entry. Applying it to aliases too is harmless: two spellings of one alias fold to the same
 * normalized signal anyway.
 */
export function ChipListEditor({
  values, onChange, max, maxLength, placeholder, suggestions = [], disabled, hex,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  max: number;
  maxLength: number;
  placeholder: string;
  suggestions?: string[];
  disabled?: boolean;
  hex: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const atMax = values.length >= max;

  const commit = (raw: string) => {
    const v = raw.trim().slice(0, maxLength);
    if (!v || atMax) return;
    if (values.some((x) => x.trim().toLowerCase() === v.toLowerCase())) { setDraft(''); return; }
    onChange([...values, v]);
    setDraft('');
  };

  const unused = suggestions.filter(
    (s) => !values.some((v) => v.trim().toLowerCase() === s.trim().toLowerCase()),
  );

  // Typeahead over the brand's existing vocabulary (2026-09-23). Tags are FOLDED on save
  // (`_resolve_tag_ids` casefolds and trims), so "Bruxelles" typed a second time with
  // different capitalisation is not a new tag — it silently joins the existing one. Saying
  // so while the user types is the only place that reads as an answer rather than a
  // surprise, and it is also what keeps the registry from filling with near-duplicates
  // nobody can then filter on.
  const q = draft.trim().toLowerCase();
  const matches = q ? suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 6) : [];
  const exact = suggestions.some((s) => s.trim().toLowerCase() === q);
  // Gated on there BEING a vocabulary: with no `suggestions` (the alias boxes) the dropdown
  // would degrade to a lone "Créer le tag" row on a field that has no tags.
  const showDropdown = !!q && suggestions.length > 0 && !atMax;

  return (
    <Box sx={{ position: 'relative' }}>
      {values.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
          {values.map((v) => (
            <Box
              key={v}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.25,
                bgcolor: alpha(hex, 0.09), color: hex,
                fontSize: '0.75rem', fontWeight: 600, pl: 0.875, pr: 0.375, py: 0.25, borderRadius: 0.75,
                maxWidth: '100%',
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</Box>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => onChange(values.filter((x) => x !== v))}
                sx={{ p: 0.125, color: alpha(hex, 0.6), '&:hover': { color: hex, bgcolor: 'transparent' } }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      <TextField
        size="small"
        fullWidth
        value={draft}
        // A comma commits too: these are pasted out of spreadsheets as comma-separated lists,
        // and the generic multi-value columns elsewhere in this page already split on commas.
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes(',')) { v.split(',').forEach(commit); return; }
          setDraft(v.slice(0, maxLength));
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(draft); }
          // Backspace on an empty box removes the last chip — the standard behaviour of every
          // tag input, and the only way to fix a typo without reaching for the mouse.
          if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
        }}
        onBlur={() => commit(draft)}
        placeholder={atMax ? t('cibles.chips.at_max', { max }) : placeholder}
        disabled={disabled || atMax}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
      />
      {showDropdown && (
        // `onMouseDown` preventDefault, not `onClick` alone: a click steals focus first, and
        // the input's `onBlur` commits the draft — so the row would add BOTH the half-typed
        // string and the suggestion.
        <Box
          onMouseDown={(e) => e.preventDefault()}
          sx={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 600, mt: 0.5,
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
            borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden',
          }}
        >
          {matches.map((s) => {
            const taken = values.some((v) => v.trim().toLowerCase() === s.trim().toLowerCase());
            return (
              <Box
                key={s}
                onClick={() => { if (!disabled && !taken) commit(s); }}
                sx={{
                  px: 1.25, py: 0.75, display: 'flex', alignItems: 'center', gap: 0.75,
                  fontSize: '0.75rem', cursor: taken ? 'default' : 'pointer',
                  color: taken ? 'text.disabled' : 'text.primary',
                  borderBottom: '1px solid', borderColor: alpha('#000', 0.04),
                  '&:hover': { bgcolor: taken ? 'transparent' : alpha(hex, 0.06) },
                }}
              >
                {taken && <CheckCircleOutlineIcon sx={{ fontSize: 13, color: hex, flexShrink: 0 }} />}
                <Box component="span" sx={{ fontWeight: 600, color: taken ? 'text.secondary' : hex }}>{s}</Box>
                {taken && <Box component="span">{t('cibles.chips.already')}</Box>}
              </Box>
            );
          })}
          {!exact && (
            <Box
              onClick={() => !disabled && commit(draft)}
              sx={{
                px: 1.25, py: 0.75, fontSize: '0.75rem', cursor: 'pointer', color: 'text.secondary',
                '&:hover': { bgcolor: alpha(hex, 0.06) },
              }}
            >
              {t('cibles.chips.create', { value: draft.trim() })}
            </Box>
          )}
        </Box>
      )}
      {unused.length > 0 && !atMax && !q && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.625, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
            {t('cibles.chips.suggestions')}
          </Typography>
          {unused.map((s) => (
            <Box
              key={s}
              onClick={() => !disabled && commit(s)}
              sx={{
                cursor: 'pointer', fontSize: '0.7rem', color: 'text.secondary',
                border: '1px dashed', borderColor: 'divider', px: 0.75, py: 0.15, borderRadius: 0.75,
                '&:hover': { borderColor: hex, color: hex },
              }}
            >
              {s}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TagChips — read-only tag chip list
// ---------------------------------------------------------------------------

/** Read-only in the list: a tag is edited in the fiche, where the whole vocabulary is offered. */
export function TagChips({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <>
      {names.map((n) => (
        <Box
          key={n}
          sx={{
            display: 'inline-flex', alignItems: 'center', maxWidth: 140,
            bgcolor: alpha('#2563EB', 0.08), color: '#1E40AF',
            fontSize: '0.7rem', fontWeight: 600, px: 0.75, py: 0.2, borderRadius: 0.5,
            lineHeight: '1.4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {n}
        </Box>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// GenericTargetDialog — add/edit dialog for all non-custom categories
// (marque, offre, produit, collection, contenu — NOT établissement or personne)
// ---------------------------------------------------------------------------

export function GenericTargetDialog({
  open, mode = 'add', cat, initialRow, marques, onSave, onClose, saving, tagRegistry,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  cat: CatConfig;
  initialRow?: TargetRow;
  marques: TargetRow[];
  onSave: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';

  const initialVals = (): Record<string, string | string[]> => {
    const out: Record<string, string | string[]> = {};
    for (const col of cat.cols) {
      if (col.multi) {
        out[col.field] = initialRow
          ? initialRow.fields.filter((f) => f.name === col.field).map((f) => String(f.value)).filter(Boolean)
          : [];
      } else {
        out[col.field] = initialRow
          ? (initialRow.fields.find((f) => f.name === col.field)?.value as string ?? '')
          : '';
      }
    }
    return out;
  };

  const [vals, setVals] = useState<Record<string, string | string[]>>(initialVals);
  const [parentId, setParentId] = useState(initialRow?.parentTargetId ?? (marques.length === 1 ? marques[0].targetId : ''));
  const [tags, setTags] = useState<string[]>(initialRow ? tagNamesOf(initialRow, tagRegistry) : []);

  useEffect(() => {
    if (!open) return;
    setVals(initialVals());
    setParentId(initialRow?.parentTargetId ?? (marques.length === 1 ? marques[0].targetId : ''));
    setTags(initialRow ? tagNamesOf(initialRow, tagRegistry) : []);
  }, [open]);

  const handleSave = async () => {
    const fields: { name: string; value: any }[] = [];
    for (const col of cat.cols) {
      const v = vals[col.field];
      if (col.multi) {
        for (const s of v as string[]) {
          if (s.trim()) fields.push({ name: col.field, value: s.trim() });
        }
      } else if ((v as string).trim()) {
        fields.push({ name: col.field, value: (v as string).trim() });
      }
    }
    const nameVal = (vals['name'] as string ?? '').trim();
    const urlVal = (vals['url'] as string ?? '').trim();
    const label = nameVal || urlVal || t('cibles.unnamed');
    await onSave({ label, parentTargetId: parentId || undefined, fields, tags });
  };

  // First required text/url field for Enter-to-save and autoFocus
  const firstRequired = cat.cols.find((c: ColDef) => !c.optional && c.type !== 'zone');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
            {isEdit ? t('cibles.actions.edit') + ' — ' + initialRow?.label : t(cat.addLabelKey)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}
          sx={{ mt: -0.5, mr: -0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Parent brand */}
        {cat.hasParent && (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.parent_column')}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                {t('cibles.col_optional')}
              </Typography>
            </Typography>
            <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
              renderValue={(value) => {
                const brand = marques.find((m) => m.targetId === value);
                if (!brand) return <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>{t('cibles.parent_none')}</Typography>;
                return <Box sx={{ display: 'inline-flex', bgcolor: alpha(cat.color, 0.1), color: alpha(cat.color, 0.9), fontWeight: 700, fontSize: '0.8rem', px: 1, py: 0.3, borderRadius: 0.75 }}>{brand.label}</Box>;
              }}
              sx={{ fontSize: '0.8125rem' }}>
              <MenuItem value="" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.parent_none')}</MenuItem>
              {marques.map((m) => <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: '0.8125rem' }}>{m.label}</MenuItem>)}
            </Select>
          </Box>
        )}

        {/* Fields */}
        {cat.cols.map((col: ColDef) => (
          <Box key={col.field}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t(col.labelKey)}
              {col.optional && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
                  {t('cibles.col_optional')}
                </Typography>
              )}
            </Typography>
            {col.multi ? (
              <ChipListEditor
                values={vals[col.field] as string[]}
                onChange={(next) => setVals((p) => ({ ...p, [col.field]: next }))}
                max={col.field === 'alias' ? TARGET_LIMITS.maxMarqueAliases : 10}
                maxLength={120}
                placeholder={t(col.placeholderKey)}
                disabled={saving}
                hex={cat.color}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                autoFocus={col.field === firstRequired?.field}
                value={vals[col.field] as string}
                onChange={(e) => setVals((p) => ({ ...p, [col.field]: e.target.value }))}
                onBlur={() => {
                  if (col.type === 'url' || col.field === 'domain') {
                    const s = vals[col.field] as string;
                    if (s) {
                      const { value } = normalizeDomainInput(s);
                      if (value) setVals((p) => ({ ...p, [col.field]: value }));
                    }
                  }
                }}
                onKeyDown={(e) => e.key === 'Enter' && !col.multi && handleSave()}
                placeholder={t(col.placeholderKey)}
                type={col.type === 'email' ? 'email' : 'text'}
                error={(col.type === 'url' || col.field === 'domain') && !!(vals[col.field] as string) && !normalizeDomainInput(vals[col.field] as string).valid}
                helperText={(col.type === 'url' || col.field === 'domain') && !!(vals[col.field] as string) && !normalizeDomainInput(vals[col.field] as string).valid ? t('cibles.url_invalid') : undefined}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
              />
            )}
          </Box>
        ))}

        {/* Tags */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.tags.label')}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>
              {t('cibles.col_optional')}
            </Typography>
          </Typography>
          <ChipListEditor
            values={tags}
            onChange={setTags}
            max={TARGET_LIMITS.maxTagsPerTarget}
            maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')}
            suggestions={[...tagRegistry.values()]}
            disabled={saving}
            hex={cat.color}
          />
        </Box>

      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving}
          sx={{ fontSize: '0.8125rem', borderRadius: 1.25 }}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave} disabled={saving}
          sx={{ fontSize: '0.8125rem', borderRadius: 1.25, bgcolor: cat.color, '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </Box>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// URL helpers for product / contenu domain views
// ---------------------------------------------------------------------------

/** Extract unique domains from a product's URL fields, capped at 3 for display. */
export function productDomains(row: TargetRow): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of row.fields) {
    if (f.name !== 'url' || typeof f.value !== 'string') continue;
    const d = extractDomain(f.value as string);
    if (d && !seen.has(d)) { seen.add(d); out.push(d); }
    if (out.length >= 3) break;
  }
  return out;
}

export function extractDomain(url: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    return new URL(full).hostname.replace(/^www\./, '');
  } catch {
    return url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0] || url;
  }
}

/**
 * Strip subdomain prefix from a host, returning the registrable apex domain.
 * `blog.dessange.com` → `dessange.com`; `dessange.co.uk` → `dessange.co.uk`.
 * Uses `registrableLabel` so two-label TLDs (co.uk, com.au…) are handled correctly.
 */
export function apexDomain(host: string): string {
  const label = registrableLabel(host);
  if (!label) return host;
  // Use lastIndexOf so a label that also appears in a subdomain (rare) doesn't truncate early
  const idx = host.lastIndexOf(label);
  return idx >= 0 ? host.slice(idx) : host;
}

export function getUrlPath(url: string, domain: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    const u = new URL(full);
    const path = u.pathname + (u.search || '') + (u.hash || '');
    return path === '/' ? '' : path;
  } catch {
    const idx = url.indexOf(domain);
    return idx >= 0 ? url.slice(idx + domain.length) : '';
  }
}

/**
 * Path to show for a URL inside a contenu group, relative to the group's apex domain.
 * For same-apex URLs: `/path` (just the pathname).
 * For subdomain URLs: `subdomain/path` — the subdomain prefix makes the source
 * identifiable within the group without repeating the apex domain.
 */
function getUrlPathInGroup(url: string, apex: string): string {
  try {
    const full = url.startsWith('http') ? url : `https://${url}`;
    const u = new URL(full);
    const host = u.hostname.replace(/^www\./, '');
    const path = u.pathname + (u.search || '') + (u.hash || '');
    const pathPart = path === '/' ? '' : path;
    if (host !== apex && host.endsWith('.' + apex)) {
      const sub = host.slice(0, -(apex.length + 1));
      return `${sub}${pathPart || '/'}`;
    }
    return pathPart || '/';
  } catch {
    return url;
  }
}

// ---------------------------------------------------------------------------
// ProductListView — one row per product with domain favicon + expandable URLs
// ---------------------------------------------------------------------------

export function ProductListView({
  rows, locked, onDelete, onEdit, marques,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!rows.length) return null;

  return (
    <Box>
      {rows.map((row) => {
        const isOpen = expanded.has(row.targetId);
        const urlFields = row.fields.filter((f) => f.name === 'url' && typeof f.value === 'string');
        const idVal = row.fields.find((f) => f.name === 'id')?.value as string | undefined;
        const domains = productDomains(row);

        // Brand color — for a very subtle bg tint and action hover
        const parentMarque = marques.find((m) => m.targetId === row.parentTargetId);
        // Use explicit colour if set, else derive a deterministic per-brand colour from the
        // marque's targetId. This ensures two brands always look distinct even without a swatch.
        const marqueColorToken = parentMarque?.color ?? null;
        const marqueHex = colorHex(marqueColorToken ?? undefined);

        return (
          <Box key={row.targetId} sx={{ borderBottom: '1px solid', borderColor: alpha('#000', 0.05),
            bgcolor: parentMarque ? alpha(marqueHex, 0.03) : 'transparent' }}>
            {/* Product row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.875 }}>

              {/* Name + ID — flex:1, truncated */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.875, overflow: 'hidden' }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.label}
                </Typography>
                {idVal && idVal !== row.label && (
                  <Box sx={{ fontSize: '0.7rem', color: 'text.disabled', fontFamily: 'monospace', bgcolor: alpha('#000', 0.05), px: 0.625, py: 0.1, borderRadius: 0.5, flexShrink: 0 }}>
                    {idVal}
                  </Box>
                )}
              </Box>

              {/* Domain favicons — horizontal, centred between name and URL count */}
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.375, alignItems: 'center', flexShrink: 0 }}>
                {domains.length > 0
                  ? domains.map((d) => <DomainFavicon key={d} domain={d} name={d} size={18} />)
                  : <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: alpha('#000', 0.06) }} />}
              </Box>

              {/* URL count + warnings */}
              {(row.warnings?.length ?? 0) > 0 && (
                <Tooltip title={row.warnings!.map((w) => w.message || w.kind).join(' · ')}>
                  <Typography sx={{ fontSize: 12, color: 'warning.main', lineHeight: 1, flexShrink: 0 }}>⚠</Typography>
                </Tooltip>
              )}
              <Box sx={{ fontSize: '0.72rem', color: 'text.disabled', fontWeight: 600, flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
                {urlFields.length} URL{urlFields.length !== 1 ? 's' : ''}
              </Box>

              {/* Actions */}
              <IconButton size="small" onClick={() => toggle(row.targetId)}
                sx={{ p: 0.375, color: 'text.disabled', transition: 'transform 0.15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>
                <Box component="span" sx={{ fontSize: 11, lineHeight: 1 }}>▶</Box>
              </IconButton>
              <IconButton size="small" disabled={locked} onClick={() => onEdit(row)}
                sx={{ p: 0.375, color: alpha('#000', 0.2), '&:hover': { color: marqueHex } }}>
                <EditOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton>
              <IconButton size="small" disabled={locked} onClick={() => onDelete(row.targetId)}
                sx={{ p: 0.375, color: alpha('#000', 0.2), '&:hover': { color: 'error.main', bgcolor: alpha('#EF4444', 0.07) } }}>
                <DeleteOutlineIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>

            {/* Expanded URL list */}
            {isOpen && urlFields.length > 0 && (
              <Box sx={{ bgcolor: alpha('#000', 0.012), borderTop: '1px solid', borderColor: alpha('#000', 0.04) }}>
                {urlFields.map((f, idx) => {
                  const url = f.value as string;
                  const full = url.startsWith('http') ? url : `https://${url}`;
                  const domain = extractDomain(url);
                  const isBuying = !!(f as any).buyingPage;
                  return (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6, pr: 1.5, py: 0.625,
                      borderBottom: idx < urlFields.length - 1 ? '1px solid' : 'none', borderColor: alpha('#000', 0.03) }}>
                      <DomainFavicon domain={domain} name={domain} size={16} />
                      <Typography
                        component="a" href={full} target="_blank" rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{ flex: 1, fontSize: '0.78rem', fontFamily: 'monospace', color: 'text.secondary',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none',
                          '&:hover': { color: '#0D9488', textDecoration: 'underline' } }}
                      >
                        {url}
                      </Typography>
                      {isBuying && (
                        <Box sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#0D9488', bgcolor: alpha('#0D9488', 0.1),
                          px: 0.625, py: 0.1, borderRadius: 0.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                          🛒 {t('cibles.produit.buying_page')}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ProductAddForm — add/edit dialog for a single product (2026-09-23)
// ---------------------------------------------------------------------------

export function ProductAddForm({
  open, mode = 'add', initialRow, marques, onSave, onClose, saving, tagRegistry,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  initialRow?: TargetRow;
  marques: TargetRow[];
  onSave: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any; buyingPage?: boolean }[]; tags?: string[] }) => Promise<void>;
  onClose: () => void;
  saving: boolean;
  tagRegistry: TagRegistry;
}) {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';
  const cat = CAT_BY_ID['produit'];

  const [parentId, setParentId] = useState('');
  const [productId, setProductId] = useState('');
  const [name, setName] = useState('');
  const [urls, setUrls] = useState<{ value: string; buyingPage: boolean }[]>([{ value: '', buyingPage: false }]);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialRow) {
      setParentId(initialRow.parentTargetId ?? '');
      setProductId((initialRow.fields.find((f) => f.name === 'id')?.value as string) ?? '');
      setName((initialRow.fields.find((f) => f.name === 'name')?.value as string) ?? '');
      setUrls(
        initialRow.fields
          .filter((f) => f.name === 'url' && typeof f.value === 'string')
          .map((f) => ({ value: f.value as string, buyingPage: !!(f as any).buyingPage }))
          .concat([]) // avoid empty list
          .filter((u) => u.value),
      );
      if (!initialRow.fields.some((f) => f.name === 'url')) setUrls([{ value: '', buyingPage: false }]);
      setTags(tagNamesOf(initialRow, tagRegistry));
    } else {
      setParentId(marques.length === 1 ? marques[0].targetId : '');
      setProductId('');
      setName('');
      setUrls([{ value: '', buyingPage: false }]);
      setTags([]);
    }
  }, [open]);

  const addUrl = () => setUrls((p) => [...p, { value: '', buyingPage: false }]);
  const removeUrl = (idx: number) => setUrls((p) => p.filter((_, i) => i !== idx));
  const setUrl = (idx: number, value: string) => setUrls((p) => p.map((u, i) => i === idx ? { ...u, value } : u));
  const setBuying = (idx: number, buyingPage: boolean) => setUrls((p) => p.map((u, i) => i === idx ? { ...u, buyingPage } : u));

  const handleSave = async () => {
    if (!productId.trim()) return;
    const validUrls = urls.filter((u) => u.value.trim());
    if (!validUrls.length) return;
    const fields: { name: string; value: any; buyingPage?: boolean }[] = [];
    fields.push({ name: 'id', value: productId.trim() });
    if (name.trim()) fields.push({ name: 'name', value: name.trim() });
    for (const u of validUrls) {
      const { value: norm } = normalizeProductUrl(u.value);
      fields.push({ name: 'url', value: norm || u.value.trim(), ...(u.buyingPage ? { buyingPage: true } : {}) });
    }
    const label = name.trim() || productId.trim();
    await onSave({ label, parentTargetId: parentId || undefined, fields, tags });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', letterSpacing: '-0.01em' }}>
            {isEdit ? t('cibles.produit.edit_title') : t('cibles.add.produit')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
            {t('cibles.produit.subtitle')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}
          sx={{ mt: -0.5, mr: -0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Brand */}
        {marques.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.parent_column')}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>{t('cibles.col_optional')}</Typography>
            </Typography>
            <Select size="small" value={parentId} onChange={(e) => setParentId(e.target.value as string)} displayEmpty fullWidth
              renderValue={(v) => { const b = marques.find((m) => m.targetId === v); return b ? <Box sx={{ display: 'inline-flex', bgcolor: alpha(cat.color, 0.1), color: alpha(cat.color, 0.9), fontWeight: 700, fontSize: '0.8rem', px: 1, py: 0.3, borderRadius: 0.75 }}>{b.label}</Box> : <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>{t('cibles.parent_none')}</Typography>; }}
              sx={{ fontSize: '0.8125rem' }}>
              <MenuItem value="" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.parent_none')}</MenuItem>
              {marques.map((m) => <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: '0.8125rem' }}>{m.label}</MenuItem>)}
            </Select>
          </Box>
        )}

        {/* ID + Name */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.produit.id_label')}
            </Typography>
            <TextField size="small" fullWidth autoFocus value={productId} onChange={(e) => setProductId(e.target.value)}
              placeholder={t('cibles.produit.id_ph')}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem', fontFamily: 'monospace' } }} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.produit.name_label')}
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>{t('cibles.col_optional')}</Typography>
            </Typography>
            <TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t('cibles.produit.name_ph')}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }} />
          </Box>
        </Box>

        {/* URLs */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.875, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.produit.urls_label')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {urls.map((u, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <TextField size="small" sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.8125rem', fontFamily: 'monospace' } }}
                  value={u.value}
                  onChange={(e) => setUrl(idx, e.target.value)}
                  onBlur={() => { const { value: v } = normalizeProductUrl(u.value); if (v) setUrl(idx, v); }}
                  onKeyDown={(e) => e.key === 'Enter' && addUrl()}
                  placeholder={t('cibles.produit.url_ph')}
                  error={!!u.value && !normalizeProductUrl(u.value).valid}
                />
                <Tooltip title={t('cibles.produit.buying_page_tooltip')}>
                  <Box
                    onClick={() => setBuying(idx, !u.buyingPage)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.375, px: 0.875, py: 0.625, borderRadius: 1,
                      border: '1px solid', borderColor: u.buyingPage ? cat.color : 'divider',
                      bgcolor: u.buyingPage ? alpha(cat.color, 0.08) : 'transparent',
                      cursor: 'pointer', userSelect: 'none', flexShrink: 0,
                      '&:hover': { borderColor: cat.color },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.9rem', lineHeight: 1 }}>🛒</Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: u.buyingPage ? 700 : 400, color: u.buyingPage ? cat.color : 'text.disabled', whiteSpace: 'nowrap' }}>
                      {t('cibles.produit.buying_page')}
                    </Typography>
                  </Box>
                </Tooltip>
                {urls.length > 1 && (
                  <IconButton size="small" onClick={() => removeUrl(idx)}
                    sx={{ flexShrink: 0, color: alpha('#000', 0.2), '&:hover': { color: 'error.main' } }}>
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>
          {urls.length < TARGET_LIMITS.maxProduitUrls && (
            <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: 13 }} />} onClick={addUrl}
              sx={{ mt: 0.75, fontSize: '0.775rem', color: cat.color, px: 0.5, '&:hover': { bgcolor: cat.tint } }}>
              {t('cibles.produit.add_url')}
            </Button>
          )}
        </Box>

        {/* Tags */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.tags.label')}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400 }}>{t('cibles.col_optional')}</Typography>
          </Typography>
          <ChipListEditor values={tags} onChange={setTags} max={TARGET_LIMITS.maxTagsPerTarget} maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')} suggestions={[...tagRegistry.values()]} disabled={saving} hex={cat.color} />
        </Box>

      </DialogContent>

      <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
        <Button variant="outlined" size="small" onClick={onClose} disabled={saving} sx={{ fontSize: '0.8125rem', borderRadius: 1.25 }}>
          {t('cibles.actions.cancel')}
        </Button>
        <Button variant="contained" size="small" disableElevation onClick={handleSave}
          disabled={saving || !productId.trim() || !urls.some((u) => u.value.trim())}
          sx={{ fontSize: '0.8125rem', borderRadius: 1.25, bgcolor: cat.color, '&:hover': { bgcolor: cat.color, filter: 'brightness(0.9)' } }}>
          {saving ? <CircularProgress size={14} color="inherit" /> : (isEdit ? t('cibles.actions.save') : t('cibles.actions.add'))}
        </Button>
      </Box>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CollectionListView — compact list for catalogue category pages (2026-09-23)
// ---------------------------------------------------------------------------

export function CollectionListView({
  rows, locked, onDelete, onEdit, marques, tagRegistry,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
  tagRegistry: TagRegistry;
}) {
  if (!rows.length) return null;
  const cat = CAT_BY_ID['collection'];

  return (
    <Box>
      {rows.map((row) => {
        const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
        const name = (row.fields.find((f) => f.name === 'name')?.value as string) ?? '';
        const domain = extractDomain(url);
        const tagNames = tagNamesOf(row, tagRegistry);
        const parentMarque = marques.find((m) => m.targetId === row.parentTargetId);
        const marqueColorToken = parentMarque?.color ?? null;
        const marqueHex = colorHex(marqueColorToken ?? undefined);
        const hasWarn = (row.warnings?.length ?? 0) > 0;

        return (
          <Box
            key={row.targetId}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              px: 2.5, py: 0.875,
              borderBottom: '1px solid', borderColor: alpha('#000', 0.05),
              bgcolor: parentMarque ? alpha(marqueHex, 0.03) : 'transparent',
            }}
          >
            {/* Domain favicon */}
            <Box sx={{ flexShrink: 0 }}>
              <DomainFavicon domain={domain} name={domain} size={20} />
            </Box>

            {/* URL — truncated, never wraps */}
            <Typography
              component="a"
              href={url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                flex: 1, minWidth: 0,
                fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                textDecoration: 'none', display: 'block',
                '&:hover': { color: cat.color, textDecoration: 'underline' },
              }}
            >
              {url}
            </Typography>

            {/* Name badge */}
            {name && (
              <Box sx={{
                fontSize: '0.75rem', fontWeight: 500, flexShrink: 0,
                bgcolor: alpha(cat.color, 0.09), color: cat.color,
                px: 0.75, py: 0.15, borderRadius: 0.625, whiteSpace: 'nowrap',
              }}>
                {name}
              </Box>
            )}

            {/* Tags */}
            {tagNames.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.375, flexShrink: 0, flexWrap: 'nowrap', overflow: 'hidden' }}>
                <TagChips names={tagNames.slice(0, 3)} />
                {tagNames.length > 3 && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', alignSelf: 'center' }}>+{tagNames.length - 3}</Typography>
                )}
              </Box>
            )}

            {/* Warning */}
            {hasWarn && (
              <Tooltip title={row.warnings!.map((w) => w.message || w.kind).join(' · ')}>
                <Typography sx={{ fontSize: 12, color: 'warning.main', lineHeight: 1, flexShrink: 0 }}>⚠</Typography>
              </Tooltip>
            )}

            {/* Actions */}
            <IconButton size="small" disabled={locked} onClick={() => onEdit(row)}
              sx={{ flexShrink: 0, p: 0.375, color: alpha('#000', 0.2), '&:hover': { color: cat.color } }}>
              <EditOutlinedIcon sx={{ fontSize: 13 }} />
            </IconButton>
            <IconButton size="small" disabled={locked} onClick={() => onDelete(row.targetId)}
              sx={{ flexShrink: 0, p: 0.375, color: alpha('#000', 0.2), '&:hover': { color: 'error.main', bgcolor: alpha('#EF4444', 0.07) } }}>
              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ContenuDomainView — domain-grouped display for the contenu tab (2026-09-23)
//
// Groups URL targets by registrable domain, shows each domain with its favicon
// and a collapsible list of paths.
// ---------------------------------------------------------------------------

export function ContenuDomainView({
  rows, locked, onDelete, onEdit, marques,
}: {
  rows: TargetRow[];
  locked: boolean;
  onDelete: (targetId: string) => void;
  onEdit: (row: TargetRow) => void;
  marques: TargetRow[];
}) {
  const { t } = useTranslation();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  // Group by brand-then-APEX-domain: subdomains collapse into the root domain.
  // `blog.dessange.com` and `dessange.com` land in the same group keyed by
  // `dessange.com`.  Key format: `<parentTargetId|"none">::<apex>` — stable, unique.
  const grouped = useMemo(() => {
    const map = new Map<string, { domain: string; parentTargetId?: string; rows: TargetRow[] }>();
    for (const row of rows) {
      const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
      const domain = apexDomain(extractDomain(url));
      const pid = row.parentTargetId ?? 'none';
      const key = `${pid}::${domain}`;
      if (!map.has(key)) map.set(key, { domain, parentTargetId: row.parentTargetId, rows: [] });
      map.get(key)!.rows.push(row);
    }
    // Sort by brand label (then domain) so the same brand stays together
    return [...map.values()].sort((a, b) => {
      const la = (marques.find((m) => m.targetId === a.parentTargetId)?.label ?? '') + a.domain;
      const lb = (marques.find((m) => m.targetId === b.parentTargetId)?.label ?? '') + b.domain;
      return la.localeCompare(lb);
    });
  }, [rows, marques]);

  const toggle = (domain: string) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain); else next.add(domain);
      return next;
    });
  };

  if (rows.length === 0) return null;

  return (
    <Box>
      {grouped.map(({ domain, parentTargetId, rows: domainRows }) => {
        const key = `${parentTargetId ?? 'none'}::${domain}`;
        const expanded = expandedDomains.has(key);
        const parentMarque = parentTargetId ? marques.find((m) => m.targetId === parentTargetId) : undefined;

        return (
          <Box key={key} sx={{ borderBottom: '1px solid', borderColor: alpha('#000', 0.05) }}>
            {/* Domain header row */}
            <Box
              onClick={() => toggle(key)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 2.5, py: 1, cursor: 'pointer', userSelect: 'none',
                '&:hover': { bgcolor: alpha('#000', 0.02) },
              }}
            >
              <DomainFavicon domain={domain} name={domain} size={22} />
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {domain}
              </Typography>
              {/* Brand badge — shown when URLs are associated with a marque */}
              {parentMarque ? (
                <Box sx={{ fontSize: '0.7rem', fontWeight: 700, bgcolor: alpha('#2563EB', 0.1), color: '#1D4ED8', px: 0.75, py: 0.15, borderRadius: 0.625, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {parentMarque.label}
                </Box>
              ) : (
                <Tooltip title={t('cibles.contenu.no_brand_hint')}>
                  <Box sx={{ fontSize: '0.68rem', color: 'text.disabled', flexShrink: 0, cursor: 'help' }}>
                    {t('cibles.contenu.no_brand')}
                  </Box>
                </Tooltip>
              )}
              <Box sx={{ bgcolor: alpha('#000', 0.07), px: 0.875, py: 0.15, borderRadius: 0.75, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                {domainRows.length}
              </Box>
              {/* Chevron */}
              <Box
                component="span"
                sx={{
                  ml: 0.25, flexShrink: 0, color: 'text.disabled', fontSize: 14, lineHeight: 1,
                  display: 'inline-block',
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}
              >
                ▶
              </Box>
            </Box>

            {/* URL list */}
            {expanded && (
              <Box sx={{ bgcolor: alpha('#000', 0.012), borderTop: '1px solid', borderColor: alpha('#000', 0.04) }}>
                {domainRows.map((row) => {
                  const url = (row.fields.find((f) => f.name === 'url')?.value as string) ?? row.label ?? '';
                  const path = getUrlPathInGroup(url, domain);
                  const hasWarn = (row.warnings?.length ?? 0) > 0;
                  return (
                    <Box
                      key={row.targetId}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        pl: 6.25, pr: 1.5, py: 0.625,
                        borderBottom: '1px solid', borderColor: alpha('#000', 0.03),
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      <Typography
                        component="a"
                        href={url.startsWith('http') ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{
                          flex: 1, fontSize: '0.8rem', fontFamily: 'monospace',
                          color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', textDecoration: 'none',
                          '&:hover': { color: '#2563EB', textDecoration: 'underline' },
                        }}
                      >
                        {path || '/'}
                      </Typography>
                      {hasWarn && (
                        <Tooltip title={row.warnings!.map((w) => w.message || w.kind).join(' · ')}>
                          <Typography sx={{ fontSize: 12, color: 'warning.main', lineHeight: 1 }}>⚠</Typography>
                        </Tooltip>
                      )}
                      <Tooltip title={t('cibles.actions.edit')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={locked}
                            onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                            sx={{ color: alpha('#000', 0.18), '&:hover': { color: '#2563EB' } }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('cibles.actions.delete')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={locked}
                            onClick={(e) => { e.stopPropagation(); onDelete(row.targetId); }}
                            sx={{ color: alpha('#000', 0.18), '&:hover': { color: 'error.main', bgcolor: alpha('#EF4444', 0.07) } }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
